export interface AnimationConfig {
  threshold: number;
  rootMargin: string;
  animationClass: string;
}

export class AnimationObserver {
  private observer: IntersectionObserver | null = null;
  private config: AnimationConfig = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
    animationClass: 'fade-in'
  };

  init(): void {
    this.setupIntersectionObserver();
    this.observeElements();
  }

  private setupIntersectionObserver(): void {
    const options: IntersectionObserverInit = {
      threshold: this.config.threshold,
      rootMargin: this.config.rootMargin
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateElement(entry.target as HTMLElement);
        }
      });
    }, options);
  }

  private observeElements(): void {
    if (!this.observer) return;

    const elementsToAnimate = document.querySelectorAll(`.${this.config.animationClass}`);
    
    elementsToAnimate.forEach(element => {
      // Set initial state
      this.setInitialState(element as HTMLElement);
      // Start observing
      this.observer!.observe(element);
    });
  }

  private setInitialState(element: HTMLElement): void {
    element.style.opacity = '0';
    element.style.transform = 'translateY(30px)';
    element.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
  }

  private animateElement(element: HTMLElement): void {
    // Trigger animation
    element.style.opacity = '1';
    element.style.transform = 'translateY(0)';
    
    // Stop observing this element
    this.observer?.unobserve(element);
  }

  public addElementToObserver(element: HTMLElement): void {
    if (!this.observer) return;
    
    this.setInitialState(element);
    this.observer.observe(element);
  }

  public removeElementFromObserver(element: HTMLElement): void {
    if (!this.observer) return;
    
    this.observer.unobserve(element);
  }

  public updateConfig(newConfig: Partial<AnimationConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Reinitialize with new config
    this.destroy();
    this.init();
  }

  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  // Utility method to manually trigger animation
  public triggerAnimation(selector: string): void {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      this.animateElement(element as HTMLElement);
    });
  }

  // Method to reset animation state
  public resetAnimation(selector: string): void {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      const htmlElement = element as HTMLElement;
      this.setInitialState(htmlElement);
      this.observer?.observe(htmlElement);
    });
  }
}